import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { indexOf, mergeAll } from "ramda";
import { useFetchVolatilityTableData } from "../../../api";
import {
  useAllPortfolios,
  useBenchmarks,
  usePeriod,
  useShowAssets,
} from "../../../store";
import { VolatilityTableRow } from "./VolatilityTableRow/VolatilityTableRow";
import {
  STableContainer,
  SGridRow1,
  SGridRow3,
  STable,
  STableBody,
  STableTitle,
  SHeaderTableRow,
  STableRow,
  SCell,
  SHeaderCell,
  SHeaderCellWithBorder,
  SFirstColumnHeaderCell,
  SCircularProgress,
  STableHead,
} from "./styles";
import { generatePortfolioColor } from "../../../utils";

export const VolatilityTable: React.FC = () => {
  const { t } = useTranslation();
  const { allPortfolios } = useAllPortfolios();
  const { selectedPeriod } = usePeriod();
  const { isAssetsShowing } = useShowAssets();
  const {
    loading: isLoading,
    data: volatilityTableData,
    fetch: fetchVolatilityTableData,
  } = useFetchVolatilityTableData();

  const { selectedBenchmarks } = useBenchmarks();

  const portfoliosAssets = useMemo(
    () =>
      mergeAll(
        Object.keys(allPortfolios).map(
          (key) => allPortfolios[Number(key)].assets
        )
      ),
    [allPortfolios]
  );

  useEffect(() => {
    fetchVolatilityTableData(allPortfolios, selectedBenchmarks, selectedPeriod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPortfolios, selectedPeriod, selectedBenchmarks]);

  return (
    <STableContainer>
      <SGridRow1>
        <STableTitle>{t("volatilityTable.title")}</STableTitle>
      </SGridRow1>

      <SGridRow3>
        <STable>
          <STableHead>
            <STableRow>
              <SHeaderCell colSpan={4} />
              <SHeaderCellWithBorder colSpan={2}>
                {t("volatilityTable.returnDays.returnDays")}
              </SHeaderCellWithBorder>
              <SHeaderCellWithBorder colSpan={5}>
                {t("volatilityTable.dailyReturn.dailyReturn")}
              </SHeaderCellWithBorder>
            </STableRow>
          </STableHead>
          <STableBody>
            <SHeaderTableRow key="volatility-header">
              <SFirstColumnHeaderCell>{t("portfolio")}</SFirstColumnHeaderCell>
              <SHeaderCell>{t("volatilityTable.yearVolatility")}</SHeaderCell>
              <SHeaderCell>{t("volatilityTable.volatility")}</SHeaderCell>
              <SHeaderCell>{t("volatilityTable.sharpeRatio")}</SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.returnDays.positive")}
              </SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.returnDays.negative")}
              </SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.dailyReturn.average")}
              </SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.dailyReturn.maximum")}
              </SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.dailyReturn.maximumDate")}
              </SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.dailyReturn.minimum")}
              </SHeaderCell>
              <SHeaderCell>
                {t("volatilityTable.dailyReturn.minimumDate")}
              </SHeaderCell>
            </SHeaderTableRow>
            {isLoading ? (
              <STableRow key="volatility-progress-key">
                <SCell colSpan={11}>
                  <SCircularProgress />
                </SCell>
              </STableRow>
            ) : (
              <React.Fragment key="volatility-assets-key">
                {Object.keys(allPortfolios).map(
                  (portfolioKey) =>
                    volatilityTableData?.[portfolioKey] && (
                      <React.Fragment key={`volatility-${portfolioKey}`}>
                        <VolatilityTableRow
                          key={portfolioKey}
                          asset={{
                            ...volatilityTableData[portfolioKey],
                            key: portfolioKey,
                            color: generatePortfolioColor(
                              Number(portfolioKey),
                              allPortfolios
                            ),
                          }}
                        />
                        {isAssetsShowing &&
                          Object.keys(
                            volatilityTableData[portfolioKey].assets
                          ).map((assetKey) => {
                            return (
                              <VolatilityTableRow
                                key={`volatility-${assetKey}`}
                                indented
                                asset={{
                                  ...volatilityTableData[portfolioKey].assets[
                                    assetKey
                                  ],
                                  key: assetKey,
                                  color: generatePortfolioColor(
                                    indexOf(
                                      assetKey,
                                      Object.keys(portfoliosAssets)
                                    ) + Object.keys(allPortfolios).length,
                                    allPortfolios
                                  ),
                                }}
                              />
                            );
                          })}
                      </React.Fragment>
                    )
                )}
                {Object.keys(selectedBenchmarks).map(
                  (benchmarkKey) =>
                    volatilityTableData?.[benchmarkKey] && (
                      <VolatilityTableRow
                        key={`volatility-${benchmarkKey}`}
                        asset={{
                          ...volatilityTableData[benchmarkKey],
                          key: benchmarkKey,
                          color: generatePortfolioColor(
                            indexOf(
                              benchmarkKey,
                              Object.keys(selectedBenchmarks)
                            ) +
                              Object.keys(allPortfolios).length +
                              Object.keys(portfoliosAssets).length,
                            allPortfolios
                          ),
                        }}
                      />
                    )
                )}
              </React.Fragment>
            )}
          </STableBody>
        </STable>
      </SGridRow3>
    </STableContainer>
  );
};
